import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient, QueryKey } from '@tanstack/react-query';
import {
  onSnapshot,
  DocumentReference,
  Query,
  DocumentSnapshot,
  QuerySnapshot,
  FirestoreError,
} from 'firebase/firestore';
import { getIdToken } from 'firebase/auth';
import { auth } from '../constants/firebaseModular';
import * as Sentry from '@sentry/browser';
import { toast } from 'sonner';
import { lastKnownIsAal2 } from '../utils/mfa/authAssurance';

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

/**
 * Auth error codes that mean THIS SESSION NO LONGER EXISTS (CU-86ak77fuv, B2).
 *
 * ⚠️ PORTED FROM `mideo-health-app` PR #310, which fixed this on device after a
 * cutover-sweep revoke produced 144 permission-denied errors in a few seconds.
 * The portal had ZERO revocation logic; the Sept 1 cutover revokes ~6469
 * sessions, and with 53 `usePatientDoc` + 53 `useChart` call sites it would fan
 * out exactly as mobile did (call sites × MAX_RETRIES). Per-listener backoff is
 * bounded; the NUMBER of listeners is not.
 *
 * ⚠️ THIS LIST IS EXHAUSTIVE ON PURPOSE — see `isRevoked` below.
 */
const REVOKED_SESSION_CODES = new Set([
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-user-token',
]);

/**
 * Did `getIdToken` fail because the SESSION IS GONE, or because the NETWORK is?
 *
 * ⚠️ THE LOAD-BEARING DISTINCTION. Treating any `getIdToken` rejection as
 * revocation turns a momentary connection blip into a permanently dead screen —
 * the opposite of the bug being fixed. #310's own mutation testing caught this:
 * the naive version passed all 7 of its tests.
 *
 * ⚠️ FAILS OPEN TOWARD RETRY, deliberately. An unrecognised code returns false
 * (retry) rather than true (stop). A wrong "stop" strands a live patient with no
 * recovery but a reload; a wrong "retry" costs at most MAX_RETRIES attempts and
 * then reports. Those costs are not symmetric.
 */
function isRevokedSessionError(error: unknown): boolean {
  const code = (error as { code?: unknown })?.code;
  return typeof code === 'string' && REVOKED_SESSION_CODES.has(code);
}

/**
 * ── B3: THE RETRY PATH REPORTED NOTHING ──────────────────────────────────
 *
 * ⚠️ MEASURED on staging: 42 listener errors, ALL at `attempt 1/3` (each from a
 * fresh mount, torn down before its 1s retry ever fired), produced 0 toasts and
 * 0 Sentry events. `captureException` lives only in the give-up branch, and
 * nothing reached it. `setContext`/`addBreadcrumb` SEND NOTHING — they decorate
 * a later event that, here, never happened. Every one of those 42 was invisible
 * outside DevTools.
 *
 * ⚠️ BUT 53 CALL SITES × A FAN-OUT MUST NOT BECOME 53 SENTRY EVENTS. So the
 * first-attempt report is throttled at MODULE scope (shared by every listener
 * instance) and fingerprinted, giving one grouped issue per window with an
 * accurate event count — rather than either silence or a flood.
 *
 * Module scope, not per-hook: the whole point is to collapse a storm that spans
 * many independent listener instances. A per-instance throttle would emit once
 * per listener, which is the flood.
 */
const FIRST_ERROR_REPORT_WINDOW_MS = 30_000;
let lastFirstErrorReportAt = 0;

/** Exported for tests only — a fan-out storm must not leak across cases. */
export function __resetListenerErrorThrottle() {
  lastFirstErrorReportAt = 0;
}

type FirestoreTarget = DocumentReference | Query;

// DEV-ONLY: Registry of active listeners for simulating errors from the browser console.
// Usage: window.__simulateListenerError('patientDoc') or window.__simulateListenerError('chart')
// This kills the active listener and triggers the full retry flow as if Firebase sent a permission-denied.
interface ListenerRegistryEntry {
  unsubscribe: () => void;
  triggerError: (_error: FirestoreError) => void;
}
const listenerRegistry = new Map<string, ListenerRegistryEntry>();

if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'staging') {
  (window as unknown as Record<string, unknown>).__simulateListenerError = (
    listenerLabel?: string,
  ) => {
    if (!listenerLabel) {
      console.log(
        '[useFirestoreListener] Active listeners:',
        Array.from(listenerRegistry.keys()).join(', '),
      );
      console.log('Usage: window.__simulateListenerError("patientDoc")');
      return;
    }
    const entry = listenerRegistry.get(listenerLabel);
    if (!entry) {
      console.error(
        `[useFirestoreListener] No active listener "${listenerLabel}". Active: ${Array.from(listenerRegistry.keys()).join(', ')}`,
      );
      return;
    }
    console.warn(`[useFirestoreListener] Simulating permission-denied on "${listenerLabel}"...`);
    // Kill the real listener (simulates Firebase terminating it on error)
    entry.unsubscribe();
    // Trigger the error handler with a synthetic FirestoreError
    const syntheticError = Object.assign(new Error('Simulated permission-denied for testing'), {
      code: 'permission-denied',
      name: 'FirebaseError',
      customData: undefined,
    }) as unknown as FirestoreError;
    entry.triggerError(syntheticError);
  };
}

type SnapshotFor<T extends FirestoreTarget> = T extends DocumentReference
  ? DocumentSnapshot
  : QuerySnapshot;

interface UseFirestoreListenerOptions<TTarget extends FirestoreTarget, TData> {
  /** The Firestore document reference or collection query to listen to. */
  target: TTarget;
  /** The React Query cache key for this data. */
  queryKey: QueryKey;
  /**
   * Transforms the raw Firestore snapshot into the shape stored in the cache.
   * - For a DocumentReference target, receives a DocumentSnapshot.
   * - For a Query target, receives a QuerySnapshot (with .docs for each document in the collection).
   * Return `undefined` to skip the cache update (e.g. for dedup).
   */
  transform: (_snapshot: SnapshotFor<TTarget>) => TData | undefined;
  /** Label used in log messages and Sentry breadcrumbs (e.g. "patientDoc", "chart"). */
  label: string;
  /** When false, the listener will not subscribe. Defaults to true. */
  enabled?: boolean;
  /**
   * Why `enabled` is false, when the reason is POLICY rather than plumbing
   * (CU-86ak77fuv, B1a).
   *
   * ⚠️ `enabled: false` alone is ambiguous, and the ambiguity is dangerous.
   * `hasResolved` has only two states, so a listener that is deliberately not
   * subscribing is indistinguishable from one that has not loaded YET:
   *
   *   hasResolved=false, enabled=true   -> still loading
   *   hasResolved=true                  -> loaded
   *   hasResolved=false, enabled=false  -> WITHHELD, reads as "still loading"
   *
   * Consumers read `hasResolved === false` as "still loading", so a gated
   * patient's chart looks PERMANENTLY loading — a spinner where the enrol screen
   * should be. Anything blocking on `hasResolved` blocks forever.
   *
   * ⚠️ THE FIX IS NOT `hasResolved = true` WHEN GATED. That would tell consumers
   * the chart loaded successfully and is EMPTY, which is worse: an empty chart
   * reads as "no data" rather than "not permitted", and the consent gate would
   * then decide on a chart it was never allowed to see.
   *
   * Set this to `true` when the data is being withheld by policy (the MFA gate).
   * Leave it `false`/absent for ordinary plumbing reasons (no uid yet, signing
   * out) — those genuinely are "not ready", not "not permitted".
   */
  withheldByPolicy?: boolean;
}

export function useFirestoreListener<TTarget extends FirestoreTarget, TData>({
  target,
  queryKey,
  transform,
  label,
  enabled = true,
  withheldByPolicy = false,
}: UseFirestoreListenerOptions<TTarget, TData>) {
  const queryClient = useQueryClient();
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  // Tracks whether the listener has received at least one snapshot from Firestore.
  // Lets consumers distinguish "still loading" from "checked and data is absent."
  const [hasResolved, setHasResolved] = useState(false);
  // CU-86ak77fuv B2 — latched when the session is gone. Stops the retry loop.
  const [sessionRevoked, setSessionRevoked] = useState(false);

  const subscribe = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    /**
     * Stop this listener for good: the session no longer exists (B2), and
     * REPORT it (B3).
     *
     * ⚠️ FINGERPRINTING IS THE LOAD-BEARING PART. With 53 `usePatientDoc` and 53
     * `useChart` call sites, a fleet-wide revocation — exactly what the Sept 1
     * cutover sweep causes — would otherwise shard ONE cause across dozens of
     * Sentry issues and hide its true event count, which is the number that
     * tells you the blast radius. `listener` stays a TAG so it is still
     * filterable without fragmenting the group.
     */
    const stopForRevokedSession = (reason: string) => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      // Park the counter at the cap so a later error on THIS instance takes the
      // give-up branch rather than starting a fresh cycle. (A remount gets a
      // fresh ref at 0 — the park does not survive it.)
      retryCountRef.current = MAX_RETRIES;
      setSessionRevoked(true);

      console.warn(`[${label}] Session revoked (${reason}) — listener stopped.`);

      Sentry.captureMessage(`Firestore listener stopped: session revoked`, {
        level: 'warning',
        tags: { listener: label, reason },
        // ⚠️ ONE issue for the whole fan-out, NOT one per listener.
        fingerprint: ['firestore-listener', 'session-revoked'],
      });
    };

    const handleError = (error: FirestoreError) => {
      const hasCachedData = queryClient.getQueryData(queryKey) !== undefined;
      const attempt = retryCountRef.current + 1;

      // Structured context for Sentry filtering/search.
      // See: https://github.com/firebase/firebase-js-sdk/issues/8686
      // Known Firebase platform issue — transient permission-denied errors
      // during token refresh or concurrent server-side writes.
      Sentry.setContext('firestoreListenerError', {
        listener: label,
        queryKey: JSON.stringify(queryKey),
        errorCode: error.code,
        errorMessage: error.message,
        attempt,
        maxRetries: MAX_RETRIES,
        hasCachedData,
        hasAuthUser: !!auth.currentUser,
      });

      Sentry.addBreadcrumb({
        category: 'firestore',
        message: `${label} listener error (attempt ${attempt}/${MAX_RETRIES})`,
        level: 'warning',
        data: {
          errorCode: error.code,
          hasCachedData,
        },
      });

      console.error(`[${label}] Firestore listener error:`, error);

      // ── CU-86ak8ytkt — THE AAL1 GATE DENIAL IS NOT A FAULT ──────────────
      //
      // ⚠️ `permission-denied` BEFORE the second factor is the rules gate
      // WORKING: it is the AAL1 refusal that keeps PHI behind AAL2. Every
      // patient meeting the gate produces one, and after the Sept 1 cutover
      // that is the entire population (~6469 first-time users) — which would
      // bury every real finding under our own instrumentation.
      //
      // ⚠️ SUPPRESSED ON THE (code, assurance) PAIR, NEVER ON THE LISTENER
      // LABEL. Suppressing by label would also hide a genuine `chart` rules
      // regression, which is the exact signal this must preserve.
      //
      // ⚠️ AND ONLY WHILE PRE-AAL2. Once the session IS AAL2 a
      // `permission-denied` means the rules are wrong for a patient who has
      // satisfied the gate — a real defect, and THE most valuable signal in the
      // hour after the production rules deploy (runbook step 12). Reporting
      // must survive there.
      //
      // ⚠️ Reads the SYNC mirror because this handler cannot await; it defaults
      // to false, so an unknown session REPORTS rather than goes silent.
      const isExpectedPreAal2Denial =
        error.code === 'permission-denied' && !lastKnownIsAal2();

      // ⚠️ B3 — REPORT THE FIRST FAILURE, not only the third. A listener torn
      // down before its retry (the measured 42) never reaches the give-up
      // branch, so without this it is reported nowhere.
      if (attempt === 1 && !isExpectedPreAal2Denial) {
        const now = Date.now();
        if (now - lastFirstErrorReportAt > FIRST_ERROR_REPORT_WINDOW_MS) {
          lastFirstErrorReportAt = now;
          Sentry.captureMessage('Firestore listener error (first attempt)', {
            level: 'warning',
            tags: { listener: label, errorCode: error.code },
            extra: { hasCachedData, hasAuthUser: !!auth.currentUser },
            // One grouped issue for the whole fan-out; the EVENT COUNT carries
            // the blast radius.
            fingerprint: ['firestore-listener', 'first-attempt', error.code],
          });
        }
      }

      if (retryCountRef.current < MAX_RETRIES) {
        const delay = BASE_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current);
        console.warn(
          `[${label}] Retrying listener (attempt ${attempt}/${MAX_RETRIES}) in ${delay}ms...`,
        );
        retryCountRef.current += 1;
        // Cancel any previously scheduled retry to avoid multiple concurrent retry timers
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(async () => {
          // Force-refresh the auth token before re-subscribing to ensure
          // the new listener doesn't reuse a stale/expired token.
          //
          // ⚠️ CU-86ak77fuv B2 — THE REFRESH OUTCOME IS NOW ACTED ON, not merely
          // reported. Both a stale token and a REVOKED one surface as the same
          // Firestore `permission-denied`; the only thing that separates them is
          // whether this refresh succeeds. The code already computed that signal
          // and then called `subscribe()` unconditionally, which is what let one
          // revoke become (call sites × MAX_RETRIES) denials.
          let tokenRefreshed = false;
          if (!auth.currentUser) {
            // No user at all — the session is gone. Nothing to retry toward.
            stopForRevokedSession('no-current-user');
            return;
          }
          try {
            await getIdToken(auth.currentUser, /* forceRefresh */ true);
            tokenRefreshed = true;
          } catch (tokenError) {
            if (isRevokedSessionError(tokenError)) {
              stopForRevokedSession(
                (tokenError as { code?: string })?.code ?? 'unknown-auth-error',
              );
              return;
            }
            // ⚠️ NETWORK (or anything unrecognised) ⇒ RETRY, NOT REVOKED. A blip
            // must not become a permanently dead screen.
            console.warn(`[${label}] Failed to refresh auth token before retry:`, tokenError);
          }
          Sentry.addBreadcrumb({
            category: 'firestore',
            message: `${label} re-subscribing after ${delay}ms delay`,
            level: 'info',
            data: { tokenRefreshed, attempt },
          });
          subscribe();
        }, delay);
      } else {
        // ⚠️ B3 — fingerprint on the ERROR CODE, not the label. A rules change
        // or an outage is ONE incident across 53 call sites, not 53 incidents.
        Sentry.captureException(
          new Error(
            `[${label}] Listener failed permanently after ${MAX_RETRIES} retries (last error: ${error.code})`,
          ),
          {
            tags: { listener: label, errorCode: error.code },
            fingerprint: ['firestore-listener', 'permanent-failure', error.code],
          },
        );
        toast.error('Error loading data. Please try again or contact support.');
        queryClient.invalidateQueries({ queryKey });
      }
    };

    unsubscribeRef.current = onSnapshot(
      // Cast required because onSnapshot's overloads for DocumentReference vs Query
      // don't unify into a single generic signature.
      target as DocumentReference,
      (snapshot) => {
        // ── ⚠️ RECOVERY MEANS THE SERVER SERVED US, NOT THAT A SNAPSHOT ARRIVED ──
        //
        // CU-86akp0tk5. This reset used to run on ANY snapshot. Where a cache can
        // answer a resubscribe, the cached snapshot resets the counter BEFORE the
        // server's denial arrives, so `MAX_RETRIES` is unreachable and a
        // rules-denial against a VALID token loops forever — no give-up, no
        // toast, and no Sentry event, because the permanent-failure capture sits
        // in that same unreachable branch.
        //
        // ⚠️ MEASURED ON MOBILE, NOT HERE. `mideo-health-app` has offline
        // persistence ON by default and looped on device 2026-09-23; its fix is
        // this same guard. The portal configures no PERSISTENT cache
        // (`firebaseModular.ts` is a bare `getFirestore`), but that does NOT mean
        // no cache. ⚙️ CODE (CU-17tm466h60x, 2026-10-16): with no `localCache`,
        // firebase 11.10.0 installs an in-memory LRU cache
        // (`LruGcMemoryOfflineComponentProvider`, "Using default
        // OfflineComponentProvider" in @firebase/firestore's index.esm2017.js).
        // ⚠️ INFERRED from SDK source, not measured: an LRU cache keeps documents
        // after their listener detaches, until GC, so a resubscribe CAN be
        // answered from cache here. This guard is live, not merely defensive.
        // An earlier version of this comment said there was no cache at all.
        //
        // ⚠️ IF YOU ENABLE PERSISTENCE, THIS GUARD IS NOT ENOUGH ON ITS OWN. With
        // `includeMetadataChanges` off (as here), a snapshot whose only change
        // is cache → server raises NO event (web SDK `QueryListener`; iOS
        // `query_listener.cc` is identical). So after a cached first snapshot,
        // a server confirmation with unchanged data never reaches this callback,
        // the counter is never reset, and a healthy session can hit the cap
        // after three unrelated blips. Decide on `includeMetadataChanges` in the
        // same change.
        //
        // PRIOR ART: `mideo-health-app` (persistence ON) hit exactly this and
        // passes `{ includeMetadataChanges: true }` (CU-86akp0tk5, commit
        // 69d2e7c). Measured on device 2026-09-30: without it, a healthy session
        // gave up after three blips; with it, the flip arrives ~130ms after the
        // cached snapshot. Cost: one extra callback per listener per resync or
        // write acknowledgement. See that file's comment at its `onSnapshot` call.
        //
        // ⚠️ ONE BEHAVIOUR CHANGE IS LIVE TODAY — bounded, and wider than first
        // thought. The same no-event rule applies even without persistence
        // whenever the FIRST snapshot after a resubscribe is cache-sourced and
        // the server's answer then matches it. Routes (from SDK source, not
        // measured): (a) the SDK's OnlineState is Offline at resubscribe — its
        // state, not the browser's; Unknown counts as online — so an EMPTY
        // cached view is raised; (b) a local write to the listened doc is still
        // pending, so a non-empty cached view is raised while online; (c) added
        // CU-17tm466h60x: the LRU memory cache above still holds the doc from an
        // earlier listener, so a non-empty cached view is raised on resubscribe.
        // "Matches" is common for (a), a doc that does not exist (e.g.
        // betaRequest) or an empty chart query, and for (c), any doc that did
        // not change while detached. Result: that instance's counter stays up
        // instead of resetting. It needs three MORE errors on that same instance
        // to give up, and the give-up is reported — so bounded, and never silent.
        //
        // ⚠️ DO NOT DELETE THE RESET. A transient error followed by genuine
        // recovery must clear the counter, or a flaky connection would exhaust
        // retries and give up on a healthy session.
        //
        // ⚠️ ABSENT metadata counts as SERVER-SOURCED. The SDK always provides
        // it, but test doubles may not — and defaulting to "cache" would stop
        // the counter from ever resetting.
        const fromCache =
          (snapshot as { metadata?: { fromCache?: boolean } }).metadata?.fromCache ?? false;
        if (!fromCache) {
          if (retryCountRef.current > 0) {
            // Listener recovered after a transient error — log which attempt succeeded
            Sentry.addBreadcrumb({
              category: 'firestore',
              message: `${label} listener recovered after ${retryCountRef.current} retry attempt(s)`,
              level: 'info',
            });
            console.info(`[${label}] Listener recovered after ${retryCountRef.current} retry attempt(s)`);
          }
          retryCountRef.current = 0;
        }
        setHasResolved(true);
        const data = transform(snapshot as SnapshotFor<TTarget>);
        if (data !== undefined) {
          queryClient.setQueryData(queryKey, data);
        }
      },
      handleError,
    );

    // Register this listener for dev-only error simulation
    listenerRegistry.set(label, {
      unsubscribe: () => unsubscribeRef.current?.(),
      triggerError: handleError,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, queryClient, ...queryKey]);

  useEffect(() => {
    if (!enabled) {
      setHasResolved(false);
      return;
    }

    subscribe();

    return () => {
      listenerRegistry.delete(label);
      setHasResolved(false);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [subscribe, label, enabled]);

  const query = useQuery<TData, Error>({
    queryKey,
    queryFn: () => new Promise<TData>(() => {}),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled,
  });

  return {
    ...query,
    hasResolved,
    /**
     * ⚠️ THE THIRD STATE (CU-86ak77fuv, B1a). True when this listener is not
     * subscribing BECAUSE THE DATA IS NOT PERMITTED — not because it is still
     * arriving. Consumers that block on `hasResolved` must check this, or a
     * gated patient sits on a spinner forever instead of reaching the gate.
     *
     * `hasResolved` stays FALSE here on purpose: the data genuinely did not
     * load, and claiming otherwise would present "not permitted" as "empty".
     */
    isWithheld: withheldByPolicy && !enabled,
    /**
     * True when the session was revoked and the listener has STOPPED. Distinct
     * from `isWithheld` (policy, session still valid) and from a transient
     * failure (still retrying).
     */
    isSessionRevoked: sessionRevoked,
  };
}
